/**
 * Clients API Service (TASK-137)
 *
 * API client functions for client management.
 */
import apiClient from './client';
import type {
  Client,
  ClientFilters,
  PaginatedClientsResponse,
  CreateClientPayload,
  UpdateClientPayload,
  IdentifyClientPayload,
  IdentifyClientResponse,
  ClientEstimate,
  ManualModeResponse,
  SendClientMessageResponse,
  ClientLink,
  ClientLinkDecision,
  LeadCard,
  SendLeadCardResponse,
  DeleteClientMessageResponse,
  DeleteClientMessageScope,
} from '@/types/client';
import type {
  SendNextBotTouchResponse,
  DraftNextBotTouchResponse,
} from '@/types/nextBotTouch';

export const clientsApi = {
  /**
   * List clients with optional filters
   */
  list: async (filters?: ClientFilters): Promise<PaginatedClientsResponse> => {
    const response = await apiClient.get<PaginatedClientsResponse>(
      '/api/v1/clients/',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get a single client by ID
   */
  getById: async (id: string): Promise<Client> => {
    const response = await apiClient.get<Client>(`/api/v1/clients/${id}/`);
    return response.data;
  },

  /**
   * Create a new client
   */
  create: async (payload: CreateClientPayload): Promise<Client> => {
    const response = await apiClient.post<Client>('/api/v1/clients/', payload);
    return response.data;
  },

  /**
   * Update a client
   */
  update: async (id: string, payload: UpdateClientPayload): Promise<Client> => {
    const response = await apiClient.patch<Client>(
      `/api/v1/clients/${id}/`,
      payload
    );
    return response.data;
  },

  /**
   * Delete a client
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/clients/${id}/`);
  },

  /**
   * Identify a client by phone or telegram_id
   */
  identify: async (
    payload: IdentifyClientPayload
  ): Promise<IdentifyClientResponse> => {
    const response = await apiClient.post<IdentifyClientResponse>(
      '/api/v1/clients/identify/',
      payload
    );
    return response.data;
  },

  /**
   * Get estimates for a client
   */
  getEstimates: async (id: string): Promise<ClientEstimate[]> => {
    const response = await apiClient.get<ClientEstimate[]>(
      `/api/v1/clients/${id}/estimates/`
    );
    return response.data;
  },

  /**
   * KAN-626: switch manual chat mode (bot silent, operator writes)
   */
  setManualMode: async (
    id: string,
    enabled: boolean
  ): Promise<ManualModeResponse> => {
    const response = await apiClient.post<ManualModeResponse>(
      `/api/v1/clients/${id}/manual-mode/`,
      { enabled }
    );
    return response.data;
  },

  /**
   * KAN-686: give a client handed to a manager back to the bot (returns the client)
   */
  returnToBot: async (id: string): Promise<Client> => {
    const response = await apiClient.post<Client>(
      `/api/v1/clients/${id}/return-to-bot/`
    );
    return response.data;
  },

  /**
   * KAN-898: move the client to junk (hidden from every list, data kept); returns the client.
   */
  junk: async (id: string): Promise<Client> => {
    const response = await apiClient.post<Client>(`/api/v1/clients/${id}/junk/`);
    return response.data;
  },

  /**
   * KAN-898: bring the client back from junk (owner only); returns the client.
   */
  unjunk: async (id: string): Promise<Client> => {
    const response = await apiClient.post<Client>(`/api/v1/clients/${id}/unjunk/`);
    return response.data;
  },

  /**
   * KAN-924: "Вернуть в работу" a client with a closed outcome (lost or won);
   * returns the client card (outcome "open"). 400 when the outcome is open or the client is junk.
   */
  reopen: async (id: string): Promise<Client> => {
    const response = await apiClient.post<Client>(`/api/v1/clients/${id}/reopen/`);
    return response.data;
  },

  /**
   * KAN-626: send a message to the client by hand (queued for delivery).
   * KAN-688: with a photo the request is multipart (text becomes the caption
   * and may be empty); without a photo it stays JSON.
   */
  sendMessage: async (
    id: string,
    text: string,
    estimateId?: string | null,
    accountId?: string | null,
    photo?: File | null,
    channel?: 'telegram' | 'max' | null
  ): Promise<SendClientMessageResponse> => {
    // KAN-641: estimate_id - the dialog open in the transcript; the message goes there.
    // KAN-663: account_id - our account chosen in the transcript; only when chosen.
    // KAN-931: channel - messenger of the chosen account; only when set.
    const body: {
      text: string;
      estimate_id?: string;
      account_id?: string;
      channel?: string;
    } = {
      text,
    };
    if (estimateId) body.estimate_id = estimateId;
    if (accountId) body.account_id = accountId;
    if (channel) body.channel = channel;
    const url = `/api/v1/clients/${id}/messages/`;
    if (!photo) {
      const response = await apiClient.post<SendClientMessageResponse>(
        url,
        body
      );
      return response.data;
    }
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => formData.append(key, value));
    formData.append('photo', photo);
    const response = await apiClient.post<SendClientMessageResponse>(
      url,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * KAN-912: delete our message of the client dialog. scope "client": in the
   * client's Telegram and in history; "history": only in history.
   */
  deleteClientMessage: async (
    clientId: string,
    messageId: string,
    scope: DeleteClientMessageScope
  ): Promise<DeleteClientMessageResponse> => {
    const response = await apiClient.delete<DeleteClientMessageResponse>(
      `/api/v1/clients/${clientId}/messages/${messageId}/`,
      { params: { scope } }
    );
    return response.data;
  },

  /**
   * KAN-631/639: links of the client card (suggested and confirmed)
   */
  getLinks: async (id: string): Promise<ClientLink[]> => {
    const response = await apiClient.get<{ results: ClientLink[] }>(
      `/api/v1/clients/${id}/links/`
    );
    return response.data.results ?? [];
  },

  /**
   * KAN-665: the lead card of the client (steps for the manager) as a structure
   */
  getLeadCard: async (id: string): Promise<LeadCard> => {
    const response = await apiClient.get<LeadCard>(
      `/api/v1/clients/${id}/lead-card/`
    );
    return response.data;
  },

  /**
   * KAN-690: send the current lead card to e-mail (returns the addresses)
   */
  sendLeadCard: async (id: string): Promise<SendLeadCardResponse> => {
    const response = await apiClient.post<SendLeadCardResponse>(
      `/api/v1/clients/${id}/lead-card/send/`
    );
    return response.data;
  },

  /**
   * KAN-631/639: owner's decision on a suggested link
   */
  decideLink: async (
    linkId: string,
    decision: ClientLinkDecision
  ): Promise<ClientLink> => {
    const response = await apiClient.post<ClientLink>(
      `/api/v1/client-links/${linkId}/decide/`,
      { decision }
    );
    return response.data;
  },

  /**
   * KAN-815: send the next bot touch of the client now (ignores only the send window)
   */
  sendNextBotTouchNow: async (id: string): Promise<SendNextBotTouchResponse> => {
    const response = await apiClient.post<SendNextBotTouchResponse>(
      `/api/v1/clients/${id}/next-bot-touch/send-now/`
    );
    return response.data;
  },

  /**
   * KAN-817: generate the text of the next bot touch now without sending it;
   * the draft is stored and sent as is when the touch runs
   */
  draftNextBotTouch: async (id: string): Promise<DraftNextBotTouchResponse> => {
    const response = await apiClient.post<DraftNextBotTouchResponse>(
      `/api/v1/clients/${id}/next-bot-touch/draft/`
    );
    return response.data;
  },

  /**
   * KAN-858: "Не писать": remove the next bot touch of the client (409 when it
   * is already running)
   */
  cancelNextBotTouch: async (id: string): Promise<DraftNextBotTouchResponse> => {
    const response = await apiClient.post<DraftNextBotTouchResponse>(
      `/api/v1/clients/${id}/next-bot-touch/cancel/`
    );
    return response.data;
  },
};

export default clientsApi;
